import React, { useState, useEffect } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import SessionList from './SessionList';
import SessionFilters from './SessionFilters';
import StatsOverview from './StatsOverview';
import ClubBreakdown from './ClubBreakdown';
import './Stats.css';

const Stats = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    clubs: [],
    distanceRange: { min: null, max: null },
    launchMonitor: '',
    location: '',
    sortBy: 'date',
    sortDirection: 'desc'
  });
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');

  useEffect(() => {
    const fetchUserAndData = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
        
        // In a real app, you would fetch the user's sessions from your backend
        // For now, we'll use mock data
        const mockSessions = generateMockSessions();
        setSessions(mockSessions);
        setFilteredSessions(mockSessions);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndData();
  }, []);

  useEffect(() => {
    // Apply filters whenever the filters or sessions change
    applyFilters();
  }, [filters, sessions]);

  const generateMockSessions = () => {
    // Generate some mock session data
    const clubs = ['Driver', '3-wood', '5-wood', '4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'PW', 'SW'];
    const locations = ['Home Range', 'Golf Galaxy', 'TopGolf', 'Club Fitting Center', 'Indoor Simulator'];
    const launchMonitors = ['Trackman', 'GCQuad', 'Flightscope', 'SkyTrak', 'Garmin R10'];
    
    const sessions = [];
    
    // Generate 10 sessions
    for (let i = 0; i < 10; i++) {
      const sessionDate = new Date();
      sessionDate.setDate(sessionDate.getDate() - i * 3 - Math.floor(Math.random() * 5)); // Random dates within the last month
      
      const location = locations[Math.floor(Math.random() * locations.length)];
      const launchMonitor = launchMonitors[Math.floor(Math.random() * launchMonitors.length)];
      
      // Generate shots for this session
      const shots = [];
      const shotCount = 20 + Math.floor(Math.random() * 30); // 20-50 shots per session
      
      for (let j = 0; j < shotCount; j++) {
        const club = clubs[Math.floor(Math.random() * clubs.length)];
        
        // Generate realistic data based on the club
        let distance, ballSpeed, clubSpeed, launchAngle, spinRate, smashFactor;
        
        if (club === 'Driver') {
          distance = 220 + Math.floor(Math.random() * 60); // 220-280 yards
          ballSpeed = 140 + Math.floor(Math.random() * 30); // 140-170 mph
          clubSpeed = 95 + Math.floor(Math.random() * 20); // 95-115 mph
          launchAngle = 10 + Math.random() * 5; // 10-15 degrees
          spinRate = 2000 + Math.floor(Math.random() * 1000); // 2000-3000 rpm
        } else if (club.includes('wood')) {
          distance = 180 + Math.floor(Math.random() * 50); // 180-230 yards
          ballSpeed = 130 + Math.floor(Math.random() * 25); // 130-155 mph
          clubSpeed = 85 + Math.floor(Math.random() * 15); // 85-100 mph
          launchAngle = 12 + Math.random() * 4; // 12-16 degrees
          spinRate = 3000 + Math.floor(Math.random() * 1000); // 3000-4000 rpm
        } else if (club.includes('iron')) {
          const ironNumber = parseInt(club.match(/\\d+/)?.[0] || '7');
          const baseDistance = 200 - (ironNumber * 10); // Rough estimate
          distance = baseDistance + Math.floor(Math.random() * 30) - 15; // +/- 15 yards
          ballSpeed = 110 + Math.floor(Math.random() * 30); // 110-140 mph
          clubSpeed = 75 + Math.floor(Math.random() * 15); // 75-90 mph
          launchAngle = 16 + Math.random() * 6; // 16-22 degrees
          spinRate = 5000 + Math.floor(Math.random() * 2000); // 5000-7000 rpm
        } else {
          // Wedges
          distance = 80 + Math.floor(Math.random() * 40); // 80-120 yards
          ballSpeed = 90 + Math.floor(Math.random() * 20); // 90-110 mph
          clubSpeed = 65 + Math.floor(Math.random() * 15); // 65-80 mph
          launchAngle = 24 + Math.random() * 8; // 24-32 degrees
          spinRate = 8000 + Math.floor(Math.random() * 2000); // 8000-10000 rpm
        }
        
        smashFactor = parseFloat((ballSpeed / clubSpeed).toFixed(2));
        
        // Add some variation for realism
        const accuracy = Math.floor(Math.random() * 40) - 20; // -20 to +20 yards (negative is left)
        
        shots.push({
          id: `shot-${i}-${j}`,
          club,
          distance,
          ballSpeed,
          clubSpeed,
          launchAngle: parseFloat(launchAngle.toFixed(1)),
          spinRate,
          smashFactor,
          accuracy,
          timestamp: new Date(sessionDate.getTime() + j * 60000) // 1 minute between shots
        });
      }
      
      // Calculate session stats
      const totalShots = shots.length;
      const avgDistance = Math.round(shots.reduce((sum, shot) => sum + shot.distance, 0) / totalShots);
      const avgBallSpeed = Math.round(shots.reduce((sum, shot) => sum + shot.ballSpeed, 0) / totalShots);
      
      sessions.push({
        id: `session-${i}`,
        date: sessionDate,
        location,
        launchMonitor,
        totalShots,
        avgDistance,
        avgBallSpeed,
        shots,
        notes: i % 3 === 0 ? 'Working on swing changes. Focusing on tempo and path.' : ''
      });
    }
    
    return sessions;
  };

  const applyFilters = () => {
    let filtered = [...sessions];
    
    // Apply date range filter
    if (filters.dateRange.start) {
      filtered = filtered.filter(session => new Date(session.date) >= new Date(filters.dateRange.start));
    }
    
    if (filters.dateRange.end) {
      filtered = filtered.filter(session => new Date(session.date) <= new Date(filters.dateRange.end));
    }
    
    // Apply location filter
    if (filters.location) {
      filtered = filtered.filter(session => 
        session.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    
    // Apply launch monitor filter
    if (filters.launchMonitor) {
      filtered = filtered.filter(session => 
        session.launchMonitor.toLowerCase().includes(filters.launchMonitor.toLowerCase())
      );
    }
    
    // Apply distance range filter
    if (filters.distanceRange.min !== null) {
      filtered = filtered.filter(session => session.avgDistance >= filters.distanceRange.min);
    }
    
    if (filters.distanceRange.max !== null) {
      filtered = filtered.filter(session => session.avgDistance <= filters.distanceRange.max);
    }
    
    // Apply club filter (this filters sessions that contain shots with the selected clubs)
    if (filters.clubs.length > 0) {
      filtered = filtered.filter(session => 
        session.shots.some(shot => filters.clubs.includes(shot.club))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      if (filters.sortBy === 'date') {
        return filters.sortDirection === 'asc' 
          ? new Date(a.date) - new Date(b.date)
          : new Date(b.date) - new Date(a.date);
      } else if (filters.sortBy === 'totalShots') {
        return filters.sortDirection === 'asc' 
          ? a.totalShots - b.totalShots
          : b.totalShots - a.totalShots;
      } else if (filters.sortBy === 'avgDistance') {
        return filters.sortDirection === 'asc' 
          ? a.avgDistance - b.avgDistance
          : b.avgDistance - a.avgDistance;
      }
      return 0;
    });
    
    setFilteredSessions(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  };

  const handleSessionSelect = (sessionId) => {
    if (selectedSession === sessionId) {
      setSelectedSession(null); // Toggle off if already selected
    } else {
      setSelectedSession(sessionId);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return <div className="stats-loading">Loading your stats...</div>;
  }

  return (
    <div className="stats-container">
      <div className="stats-header">
        <h1>Golf Stats</h1>
        <p>Analyze your performance data and track your progress over time.</p>
      </div>
      
      <div className="stats-tabs">
        <button 
          className={activeTab === 'sessions' ? 'active' : ''} 
          onClick={() => handleTabChange('sessions')}
        >
          Range Sessions
        </button>
        <button 
          className={activeTab === 'overview' ? 'active' : ''} 
          onClick={() => handleTabChange('overview')}
        >
          Stats Overview
        </button>
        <button 
          className={activeTab === 'clubs' ? 'active' : ''} 
          onClick={() => handleTabChange('clubs')}
        >
          Club Breakdown
        </button>
      </div>
      
      <div className="stats-content">
        {activeTab === 'sessions' && (
          <>
            <SessionFilters 
              filters={filters} 
              onFilterChange={handleFilterChange} 
              sessions={sessions}
            />
            
            <div className="session-results-summary">
              Showing {filteredSessions.length} of {sessions.length} sessions
            </div>
            
            <SessionList 
              sessions={filteredSessions} 
              selectedSession={selectedSession}
              onSessionSelect={handleSessionSelect}
            />
          </>
        )}
        
        {activeTab === 'overview' && (
          <StatsOverview sessions={sessions} />
        )}
        
        {activeTab === 'clubs' && (
          <ClubBreakdown sessions={sessions} />
        )}
      </div>
    </div>
  );
};

export default Stats;
